import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export const useGlobalQueryString = () => {
  const queryClient = useQueryClient();
  const [queryString, setQueryString] = useState<string>('');

  useEffect(() => {
    const updateQueryString = () => {
      // This runs from a cache subscription, including on QueryCache.add, at
      // which point an entry exists but its data can still be undefined. The
      // previous `!== null` check let undefined through and threw on
      // `.toString()`, taking down whichever page happened to register a new
      // query-state key. A nullish check covers both, and the key itself is
      // verified since queryKey[2] is only typed as a string.
      const searchParams = new URLSearchParams();

      for (const cache of queryClient.getQueryCache().getAll()) {
        if (cache.queryKey[1] !== 'QUERY_STATE') continue;

        const key = cache.queryKey[2];
        const data = cache.state.data as string | number | null | undefined;
        if (typeof key !== 'string' || !key) continue;
        if (data == null || data === '') continue;

        searchParams.append(key, String(data));
      }

      setQueryString(searchParams.toString());
    };

    updateQueryString();

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[1] === 'QUERY_STATE') {
        updateQueryString();
      }
    });

    return unsubscribe;
  }, [queryClient]);

  return queryString;
};
